import type { ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import type { v4 } from '@rotra-air-ocean/rotranext-shared-code';
import { Check, Minus, Plus, Rocket, X } from 'lucide-react';

const GROUPS: {
  id: v4.WorkflowChangeGroup;
  label: string;
}[] = [
  { id: 'workflow', label: 'Workflow details' },
  { id: 'steps', label: 'Steps added, removed or renamed' },
  { id: 'configuration', label: 'Step configuration' },
  { id: 'routing', label: 'Routing' },
  { id: 'inputsOutputs', label: 'Inputs and outputs' },
];

function ChangeKind({ kind }: { kind: v4.WorkflowChangeKind }) {
  if (kind === 'added') {
    return <Plus size={14} />;
  }
  if (kind === 'removed') {
    return <Minus size={14} />;
  }
  return <Check size={14} />;
}

function ChangeRow({ change }: { change: v4.WorkflowChange }) {
  let color: 'error' | 'success' | 'warning' = 'warning';
  if (change.kind === 'removed') {
    color = 'error';
  } else if (change.kind === 'added') {
    color = 'success';
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'flex-start',
      }}
    >
      <Chip
        size="small"
        color={color}
        icon={<ChangeKind kind={change.kind} />}
        label={change.kind}
        sx={{ minWidth: 82, textTransform: 'capitalize' }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {change.title}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}
        >
          {change.detail}
        </Typography>
        {change.before !== undefined && (
          <Typography
            variant="body2"
            sx={{ mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            <strong>Before:</strong> {change.before}
          </Typography>
        )}
        {change.after !== undefined && (
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            <strong>After:</strong> {change.after}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

export function WorkflowActivationReviewDialog({
  open,
  comparison,
  validation,
  title,
  onClose,
  onConfirm,
  confirming = false,
}: {
  open: boolean;
  comparison: v4.WorkflowComparison | null;
  validation: v4.WorkflowValidationResult | null;
  title: string;
  onClose: () => void;
  onConfirm: () => void;
  confirming?: boolean;
}) {
  let confirmationLabel = 'Activate version';
  if (validation?.valid === false) {
    confirmationLabel = 'Resolve blockers first';
  }
  if (confirming) {
    confirmationLabel = 'Activating…';
  }
  const changes = comparison?.changes ?? [];
  let content: ReactNode;
  if (!comparison) {
    content = (
      <Typography
        sx={{
          color: 'text.secondary',
        }}
      >
        Preparing comparison…
      </Typography>
    );
  } else if (changes.length === 0) {
    content = (
      <Alert severity="success">
        No functional changes were found between these versions.
      </Alert>
    );
  } else {
    content = GROUPS.map((group) => {
      const groupChanges = changes.filter(
        (change) => change.group === group.id,
      );
      if (groupChanges.length === 0) {
        return null;
      }
      return (
        <Box key={group.id}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            {group.label}
          </Typography>
          <Stack spacing={1.25}>
            {groupChanges.map((change, index) => (
              <ChangeRow key={`${change.title}-${index}`} change={change} />
            ))}
          </Stack>
          <Divider sx={{ mt: 2 }} />
        </Box>
      );
    });
  }

  return (
    <Dialog
      open={open}
      onClose={confirming ? undefined : onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}
      >
        <Rocket size={19} />
        Review activation of {title}
        <IconButton
          onClick={onClose}
          disabled={confirming}
          sx={{ ml: 'auto' }}
          aria-label="Close"
        >
          <X size={17} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity={comparison?.initialActivation ? 'info' : 'warning'}>
            {comparison?.initialActivation
              ? `Version ${comparison.toVersion} will become the first live version of this workflow.`
              : `Version ${comparison?.toVersion} will replace version ${comparison?.fromVersion}. Review the changes before making it live.`}
          </Alert>

          {validation?.valid === false && (
            <Alert severity="error">
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Activation is blocked by workflow configuration
              </Typography>
              <Box component="ul" sx={{ mb: 0, mt: 0.5, pl: 2.5 }}>
                {validation.issues.map((issue, index) => (
                  <li
                    key={`${issue.code}-${issue.dependencyPath.join(
                      '-',
                    )}-${index}`}
                  >
                    {issue.message}
                    {validation.affectedLiveParentIds.includes(
                      issue.rootWorkflowId,
                    )
                      ? ` Checked through live workflow ${issue.rootWorkflowId}.`
                      : ''}
                  </li>
                ))}
              </Box>
            </Alert>
          )}

          {validation?.valid && validation.affectedLiveParentIds.length > 0 && (
            <Alert severity="success">
              Compatible with live parent workflows:{' '}
              {validation.affectedLiveParentIds.join(', ')}.
            </Alert>
          )}

          {content}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={confirming}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Rocket size={15} />}
          onClick={onConfirm}
          disabled={!comparison || !validation?.valid || confirming}
        >
          {confirmationLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
